import { useEffect, useState, type FormEvent } from 'react';
import { useAdmin } from '../../components/AdminLayout';
import { api, type AdminConfigEntry } from '../../lib/api';
import s from './AdminConfig.module.css';

// ---------------------------------------------------------------------------
// Pricing plans sub-component
// ---------------------------------------------------------------------------

interface PlanFields {
  name: string;
  price: number;
  maxDomains: number;
  weeklyReportLimit: number;
  features: string[];
}

function PricingPlansEditor({
  value,
  editing,
  onChange,
}: {
  value: string;
  editing: boolean;
  onChange: (newValue: string) => void;
}) {
  let plans: PlanFields[] = [];
  try {
    const parsed = JSON.parse(value);
    plans = Array.isArray(parsed)
      ? parsed
      : Object.entries(parsed).map(([name, v]: [string, any]) => ({
          name,
          price: v.price ?? 0,
          maxDomains: v.max_domains ?? v.maxDomains ?? 0,
          weeklyReportLimit: v.weekly_report_limit ?? v.weeklyReportLimit ?? 0,
          features: v.features ?? [],
        }));
  } catch {
    // If parsing fails, show as raw JSON
    return null;
  }

  function updatePlan(index: number, field: keyof PlanFields, val: any) {
    const updated = plans.map((p, i) => {
      if (i !== index) return p;
      return { ...p, [field]: val };
    });
    onChange(JSON.stringify(updated, null, 2));
  }

  return (
    <div className={s.plansSection}>
      <div className={s.planGrid}>
        {plans.map((plan, i) => (
          <div key={plan.name} className={s.planCard}>
            <div className={s.planName}>{plan.name}</div>

            <div className={s.planField}>
              <label className={s.planLabel}>Price ($/mo)</label>
              <input
                type="number"
                className={s.planInput}
                value={plan.price}
                disabled={!editing}
                onChange={(e) => updatePlan(i, 'price', parseFloat(e.target.value) || 0)}
                min={0}
              />
            </div>

            <div className={s.planField}>
              <label className={s.planLabel}>Max Domains</label>
              <input
                type="number"
                className={s.planInput}
                value={plan.maxDomains}
                disabled={!editing}
                onChange={(e) => updatePlan(i, 'maxDomains', parseInt(e.target.value, 10) || 0)}
                min={0}
              />
            </div>

            <div className={s.planField}>
              <label className={s.planLabel}>Weekly Report Limit</label>
              <input
                type="number"
                className={s.planInput}
                value={plan.weeklyReportLimit}
                disabled={!editing}
                onChange={(e) =>
                  updatePlan(i, 'weeklyReportLimit', parseInt(e.target.value, 10) || 0)
                }
                min={0}
              />
            </div>

            <div className={s.planField}>
              <label className={s.planLabel}>Features (comma-separated)</label>
              <input
                type="text"
                className={s.planInput}
                value={plan.features.join(', ')}
                disabled={!editing}
                onChange={(e) =>
                  updatePlan(
                    i,
                    'features',
                    e.target.value.split(',').map((f) => f.trim()).filter(Boolean),
                  )
                }
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config card
// ---------------------------------------------------------------------------

function ConfigCard({
  entry,
  secret,
  onUpdated,
}: {
  entry: AdminConfigEntry;
  secret: string;
  onUpdated: (entry: AdminConfigEntry) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(entry.value);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isPricingPlans = entry.key === 'pricing_plans';

  function startEdit() {
    setEditValue(formatJson(entry.value));
    setEditing(true);
    setMsg(null);
  }

  function cancelEdit() {
    setEditing(false);
    setEditValue(entry.value);
    setMsg(null);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);

    try {
      // Validate JSON if it looks like JSON
      const trimmed = editValue.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        JSON.parse(trimmed); // throws if invalid
      }

      const result = await api.admin.updateConfig(secret, entry.key, trimmed);
      onUpdated(result.entry);
      setEditing(false);
      setMsg({ type: 'success', text: 'Saved.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      setMsg({
        type: 'error',
        text: err instanceof Error ? err.message : 'Save failed',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>
        <span className={s.cardKey}>{entry.key}</span>
        <span className={s.cardUpdated}>
          Updated: {new Date(entry.updatedAt).toLocaleString()}
        </span>
      </div>

      {entry.description && (
        <div className={s.cardDescription}>{entry.description}</div>
      )}

      {msg && (
        <div className={msg.type === 'success' ? s.successMsg : s.error}>
          {msg.text}
        </div>
      )}

      {/* Value display / editor */}
      {isPricingPlans && (
        <PricingPlansEditor
          value={editing ? editValue : entry.value}
          editing={editing}
          onChange={setEditValue}
        />
      )}

      {!editing ? (
        <div className={s.codeBlock}>{formatJson(entry.value)}</div>
      ) : (
        <form onSubmit={handleSave}>
          {!isPricingPlans && (
            <textarea
              className={s.textarea}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={Math.max(4, editValue.split('\n').length + 1)}
            />
          )}

          <div className={s.cardActions}>
            <button type="submit" className={s.btnSave} disabled={saving}>
              {saving ? 'Saving\u2026' : 'Save'}
            </button>
            <button type="button" className={s.btnCancel} onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {!editing && (
        <div className={s.cardActions}>
          <button type="button" className={s.btnEdit} onClick={startEdit}>
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatJson(value: string): string {
  try {
    const parsed = JSON.parse(value);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AdminConfig() {
  const { secret } = useAdmin();

  const [entries, setEntries] = useState<AdminConfigEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.admin
      .config(secret)
      .then(({ config }) => {
        if (!cancelled) {
          setEntries(config);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load config');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [secret]);

  function handleUpdated(updated: AdminConfigEntry) {
    setEntries((prev) =>
      prev.map((e) => (e.key === updated.key ? updated : e)),
    );
  }

  if (loading) {
    return <div className={s.loading}>Loading configuration&hellip;</div>;
  }

  if (error) {
    return <div className={s.error}>{error}</div>;
  }

  return (
    <div className={s.page}>
      <h1 className={s.heading}>System Configuration</h1>

      <div className={s.cardList}>
        {entries.map((entry) => (
          <ConfigCard
            key={entry.key}
            entry={entry}
            secret={secret}
            onUpdated={handleUpdated}
          />
        ))}

        {entries.length === 0 && (
          <div className={s.loading}>No configuration entries found.</div>
        )}
      </div>
    </div>
  );
}
